/**
 * Category Service - Business Logic Layer
 * 
 * This service handles BUSINESS DATA only:
 * - Category types (identifiers)
 * - User preferences (custom names, visibility, sort order)
 * - CRUD operations
 * 
 * NO PRESENTATION DATA (icons, colors) - that lives in category-config.ts
 * 
 * Architecture:
 * Database → Service → View Models (with presentation) → UI
 */

import { getSupabase } from "../../database";
import type { UserCategory, CategoryViewModel, CreateCategoryDto, UpdateCategoryDto } from "../types";
import type { CategoryType } from "./category-config";
import {
    getCategoryPresentation,
    getAllCategoryTypes,
    isValidCategoryType
} from "./category-config";

// Database type for user_categories table (matches new schema)
interface DatabaseUserCategory {
    id: string;
    user_id: string;
    category_type: string;
    custom_name: string | null;
    is_hidden: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

// Transform database row to UserCategory (business data only)
function toUserCategory(db: DatabaseUserCategory): UserCategory {
    // custom_name should always be present (NOT NULL in DB)
    // This fallback handles any legacy data
    const customName = db.custom_name || 'Unnamed Category';

    return {
        id: db.id,
        userId: db.user_id,
        categoryType: db.category_type as CategoryType,
        customName: customName,
        isHidden: db.is_hidden,
        sortOrder: db.sort_order,
        createdAt: db.created_at,
        updatedAt: db.updated_at,
    };
}

// Transform UserCategory to database row
function toDatabase(category: Partial<UserCategory> & { userId: string }): Partial<DatabaseUserCategory> {
    const db: Partial<DatabaseUserCategory> = {
        user_id: category.userId,
    };

    if (category.categoryType !== undefined) db.category_type = category.categoryType;
    if (category.customName !== undefined) db.custom_name = category.customName;
    if (category.isHidden !== undefined) db.is_hidden = category.isHidden;
    if (category.sortOrder !== undefined) db.sort_order = category.sortOrder;

    return db;
}

/**
 * Merge business data with presentation config to create view model
 * This is where we combine database data with UI presentation
 */
function toViewModel(category: UserCategory, theme: "light" | "dark" = "light"): CategoryViewModel {
    const presentation = getCategoryPresentation(category.categoryType, theme);

    return {
        ...category,
        displayName: category.customName, // customName is always set (required field)
        icon: presentation.icon,
        color: presentation.color,
        description: presentation.description,
    };
}

export class CategoryService {
    /**
     * Get all categories for a user (returns business data only)
     */
    static async getAllCategories(userId: string): Promise<UserCategory[]> {
        const supabase = getSupabase();

        const { data, error } = await supabase
            .from('user_categories')
            .select('*')
            .eq('user_id', userId)
            .order('sort_order', { ascending: true });

        if (error) {
            throw new Error(`Failed to fetch categories: ${error.message}`);
        }

        return (data || []).map(toUserCategory);
    }

    /**
     * Get all categories with presentation data for UI (view models)
     */
    static async getAllCategoriesWithPresentation(
        userId: string,
        theme: "light" | "dark" = "light"
    ): Promise<CategoryViewModel[]> {
        const categories = await this.getAllCategories(userId);
        return categories.map(cat => toViewModel(cat, theme));
    }

    /**
     * Get only visible categories (not hidden)
     */
    static async getVisibleCategories(userId: string): Promise<UserCategory[]> {
        const supabase = getSupabase();

        const { data, error } = await supabase
            .from('user_categories')
            .select('*')
            .eq('user_id', userId)
            .eq('is_hidden', false)
            .order('sort_order', { ascending: true });

        if (error) {
            throw new Error(`Failed to fetch visible categories: ${error.message}`);
        }

        return (data || []).map(toUserCategory);
    }

    /**
     * Get visible categories with presentation data for UI
     */
    static async getVisibleCategoriesWithPresentation(
        userId: string,
        theme: "light" | "dark" = "light"
    ): Promise<CategoryViewModel[]> {
        const categories = await this.getVisibleCategories(userId);
        return categories.map(cat => toViewModel(cat, theme));
    }

    /**
     * Get recently used categories based on expense history
     */
    static async getRecentCategories(userId: string, limit: number = 5): Promise<UserCategory[]> {
        const supabase = getSupabase();

        // Get most recent category IDs from expenses
        const { data: recentExpenses, error: expenseError } = await supabase
            .from('expenses')
            .select('category_id')
            .eq('user_id', userId)
            .not('category_id', 'is', null)
            .order('created_at', { ascending: false })
            .limit(50); // Look at last 50 expenses

        if (expenseError) {
            throw new Error(`Failed to fetch recent expenses: ${expenseError.message}`);
        }

        if (!recentExpenses || recentExpenses.length === 0) {
            return [];
        }

        // Get unique category IDs in order of most recent use
        const categoryIds = Array.from(new Set(
            recentExpenses
                .map(e => e.category_id)
                .filter(Boolean)
        )).slice(0, limit);

        if (categoryIds.length === 0) {
            return [];
        }

        // Fetch the actual category data
        const { data: categories, error: categoryError } = await supabase
            .from('user_categories')
            .select('*')
            .in('id', categoryIds)
            .eq('is_hidden', false);

        if (categoryError) {
            throw new Error(`Failed to fetch recent categories: ${categoryError.message}`);
        }

        // Sort by the order they appear in categoryIds
        const categoriesMap = new Map((categories || []).map(c => [c.id, toUserCategory(c)]));
        return categoryIds.map(id => categoriesMap.get(id)).filter(Boolean) as UserCategory[];
    }

    /**
     * Get recently used categories with presentation data
     */
    static async getRecentCategoriesWithPresentation(
        userId: string,
        limit: number = 5,
        theme: "light" | "dark" = "light"
    ): Promise<CategoryViewModel[]> {
        const categories = await this.getRecentCategories(userId, limit);
        return categories.map(cat => toViewModel(cat, theme));
    }

    /**
     * Create a new custom category
     * Note: User must provide a custom name (required)
     * Multiple categories can share the same type (e.g., multiple "housing" categories)
     * Icons/colors come from presentation config based on type
     */
    static async createCategory(userId: string, dto: CreateCategoryDto): Promise<UserCategory> {
        const supabase = getSupabase();

        // Validate category type
        if (!isValidCategoryType(dto.categoryType)) {
            throw new Error(`Invalid category type: ${dto.categoryType}`);
        }

        // Validate custom name is provided and not empty
        if (!dto.customName || !dto.customName.trim()) {
            throw new Error('Category name is required');
        }

        // Check if user already has a category with this exact name
        const { data: existing } = await supabase
            .from('user_categories')
            .select('id')
            .eq('user_id', userId)
            .eq('custom_name', dto.customName.trim())
            .single();

        if (existing) {
            throw new Error(`You already have a category named "${dto.customName}"`);
        }

        // Get the highest sort_order to append new category at the end
        const { data: existingCategories } = await supabase
            .from('user_categories')
            .select('sort_order')
            .eq('user_id', userId)
            .order('sort_order', { ascending: false })
            .limit(1);

        const maxSortOrder = existingCategories && existingCategories.length > 0
            ? existingCategories[0].sort_order
            : 0;

        const newCategory = {
            user_id: userId,
            category_type: dto.categoryType,
            custom_name: dto.customName.trim(),
            is_hidden: false,
            sort_order: maxSortOrder + 1,
        };

        const { data, error } = await supabase
            .from('user_categories')
            .insert(newCategory)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to create category: ${error.message}`);
        }

        return toUserCategory(data);
    }

    /**
     * Update an existing category
     * Users can update: custom name, visibility, sort order
     * Users CANNOT update: category type (that's immutable), icons, colors (in config)
     */
    static async updateCategory(categoryId: string, userId: string, dto: UpdateCategoryDto): Promise<UserCategory> {
        const supabase = getSupabase();

        // Ensure user can only update their own categories
        const { data: existing } = await supabase
            .from('user_categories')
            .select('id')
            .eq('id', categoryId)
            .eq('user_id', userId)
            .single();

        if (!existing) {
            throw new Error('Category not found');
        }

        const updates = toDatabase({ userId, ...dto });
        updates.updated_at = new Date().toISOString();

        const { data, error } = await supabase
            .from('user_categories')
            .update(updates)
            .eq('id', categoryId)
            .eq('user_id', userId)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to update category: ${error.message}`);
        }

        return toUserCategory(data);
    }

    /**
     * Hide a category
     */
    static async hideCategory(categoryId: string, userId: string): Promise<void> {
        const supabase = getSupabase();

        const { error } = await supabase
            .from('user_categories')
            .update({ is_hidden: true, updated_at: new Date().toISOString() })
            .eq('id', categoryId)
            .eq('user_id', userId);

        if (error) {
            throw new Error(`Failed to hide category: ${error.message}`);
        }
    }

    /**
     * Show a hidden category
     */
    static async showCategory(categoryId: string, userId: string): Promise<void> {
        const supabase = getSupabase();

        const { error } = await supabase
            .from('user_categories')
            .update({ is_hidden: false, updated_at: new Date().toISOString() })
            .eq('id', categoryId)
            .eq('user_id', userId);

        if (error) {
            throw new Error(`Failed to show category: ${error.message}`);
        }
    }

    /**
     * Delete a category (soft delete by hiding, or hard delete)
     */
    static async deleteCategory(categoryId: string, userId: string, hard: boolean = false): Promise<void> {
        const supabase = getSupabase();

        // Ensure user can only delete their own categories
        const { data: existing } = await supabase
            .from('user_categories')
            .select('id')
            .eq('id', categoryId)
            .eq('user_id', userId)
            .single();

        if (!existing) {
            throw new Error('Category not found');
        }

        if (hard) {
            // Hard delete - remove from database
            const { error } = await supabase
                .from('user_categories')
                .delete()
                .eq('id', categoryId)
                .eq('user_id', userId);

            if (error) {
                throw new Error(`Failed to delete category: ${error.message}`);
            }
        } else {
            // Soft delete - just hide it
            await this.hideCategory(categoryId, userId);
        }
    }

    /**
     * Search categories by name (searches both custom names and default names)
     */
    static async searchCategories(query: string, userId: string, theme: "light" | "dark" = "light"): Promise<CategoryViewModel[]> {
        // Get all visible categories
        const categories = await this.getVisibleCategories(userId);

        // Convert to view models and filter by name match
        const viewModels = categories.map(cat => toViewModel(cat, theme));
        const lowerQuery = query.toLowerCase();

        return viewModels.filter(vm =>
            vm.displayName.toLowerCase().includes(lowerQuery)
        );
    }

    /**
     * Get category usage statistics
     */
    static async getCategoryStats(
        userId: string,
        theme: "light" | "dark" = "light"
    ): Promise<Array<{ category: CategoryViewModel; count: number; total: number }>> {
        const supabase = getSupabase();

        // Get all expenses with category IDs
        const { data: expenses, error: expenseError } = await supabase
            .from('expenses')
            .select('category_id, amount')
            .eq('user_id', userId)
            .not('category_id', 'is', null);

        if (expenseError) {
            throw new Error(`Failed to fetch expenses: ${expenseError.message}`);
        }

        // Get all user categories
        const categories = await this.getAllCategories(userId);
        const categoryMap = new Map(categories.map(c => [c.id, c]));

        // Aggregate by category
        const stats = new Map<string, { count: number; total: number }>();

        (expenses || []).forEach(expense => {
            if (expense.category_id) {
                const existing = stats.get(expense.category_id);
                if (existing) {
                    existing.count++;
                    existing.total += expense.amount || 0;
                } else {
                    stats.set(expense.category_id, {
                        count: 1,
                        total: expense.amount || 0,
                    });
                }
            }
        });

        return Array.from(stats.entries())
            .map(([categoryId, data]) => {
                const category = categoryMap.get(categoryId);
                if (!category) return null;

                return {
                    category: toViewModel(category, theme),
                    count: data.count,
                    total: data.total,
                };
            })
            .filter(Boolean) as Array<{ category: CategoryViewModel; count: number; total: number }>;
    }

    /**
     * Initialize all system categories for a new user
     * Creates a user_categories record for each system category type
     */
    static async initializeSystemCategories(userId: string): Promise<void> {
        const supabase = getSupabase();

        // Check if user already has categories
        const { data: existing } = await supabase
            .from('user_categories')
            .select('id')
            .eq('user_id', userId)
            .limit(1);

        if (existing && existing.length > 0) {
            // User already has categories initialized
            return;
        }

        // Insert all system category types
        const systemCategories = getAllCategoryTypes().map((type, index) => ({
            user_id: userId,
            category_type: type,
            custom_name: null, // Use default name from config
            is_hidden: false,
            sort_order: index + 1,
        }));

        const { error } = await supabase
            .from('user_categories')
            .insert(systemCategories);

        if (error) {
            throw new Error(`Failed to initialize system categories: ${error.message}`);
        }
    }

    /**
     * Get a single category by ID with presentation
     */
    static async getCategoryById(
        categoryId: string,
        userId: string,
        theme: "light" | "dark" = "light"
    ): Promise<CategoryViewModel | null> {
        const supabase = getSupabase();

        const { data, error } = await supabase
            .from('user_categories')
            .select('*')
            .eq('id', categoryId)
            .eq('user_id', userId)
            .single();

        if (error || !data) {
            return null;
        }

        return toViewModel(toUserCategory(data), theme);
    }
}

